import { useEffect, useRef, useCallback } from "react"

const UNICORN_SDK_URL =
  "https://cdn.jsdelivr.net/gh/hiunicornstudio/unicornstudio.js@v2.0.5/dist/unicornStudio.umd.js"

export const LUSTER_SHADER_SLOT_WIDTH = 800
export const LUSTER_SHADER_SLOT_HEIGHT = 300
export const BENTO_SHADER_SLOT_HEIGHT = 140

type UnicornSDK = {
  init: (opts?: { element?: HTMLElement }) => Promise<unknown[]>
  destroy: () => void
}

function getSDK(): UnicornSDK | null {
  return (window as unknown as { UnicornStudio?: UnicornSDK }).UnicornStudio ?? null
}

function loadUnicornScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve()
  if (getSDK()) return Promise.resolve()

  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${UNICORN_SDK_URL}"]`)
    if (existing) {
      if (getSDK()) { resolve(); return }
      existing.addEventListener("load", () => resolve())
      return
    }
    const script = document.createElement("script")
    script.src = UNICORN_SDK_URL
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error("Failed to load Unicorn Studio SDK"))
    ;(document.head || document.body).appendChild(script)
  })
}

type LusterShaderSlotProps = {
  minHeight?: number
  scale?: number
  fps?: number
  lazyLoad?: boolean
  fill?: boolean
  className?: string
}

/**
 * Embeds the Luster dashboard hero shader (Unicorn Studio).
 *
 * fill=true: host div fills its container (absolute inset-0). Unicorn renders in
 * normalized UV space so UV center (0.5, 0.5) = container center — always, on every
 * screen size, with zero JS math.
 */
export function LusterShaderSlot({
  minHeight = BENTO_SHADER_SLOT_HEIGHT,
  scale = 1,
  fps = 30,
  lazyLoad = true,
  fill = false,
  className,
}: LusterShaderSlotProps) {
  const hostRef = useRef<HTMLDivElement>(null)
  const scenesRef = useRef<unknown[]>([])

  const destroyScenes = useCallback(() => {
    scenesRef.current.forEach((s: unknown) => {
      try { (s as { destroy?: () => void }).destroy?.() } catch { /* noop */ }
    })
    scenesRef.current = []
  }, [])

  useEffect(() => {
    let cancelled = false

    const run = async () => {
      await loadUnicornScript()
      if (cancelled || !hostRef.current) return
      const sdk = getSDK()
      if (!sdk) return
      requestAnimationFrame(() => {
        if (cancelled || !hostRef.current) return
        sdk.init({ element: hostRef.current }).then((instances) => {
          if (!cancelled) scenesRef.current = instances ?? []
        }).catch(() => {})
      })
    }

    run()
    return () => {
      cancelled = true
      destroyScenes()
    }
  }, [destroyScenes])

  useEffect(() => {
    const el = hostRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          scenesRef.current.forEach((s: unknown) => {
            const scene = s as { pause?: () => void; resume?: () => void }
            try {
              if (entry.isIntersecting) {
                scene.resume?.()
              } else {
                scene.pause?.()
              }
            } catch { /* noop */ }
          })
        }
      },
      { threshold: 0 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const sharedAttrs = {
    "data-us-project-src": "/shaders/luster-shader-slot-800x300.json",
    "data-us-scale": String(scale),
    "data-us-fps": String(fps),
    ...(lazyLoad ? { "data-us-lazyload": "true" } : {}),
    "data-us-alttext": "Luster Amplitude hero shader",
    "data-us-arialabel": "Decorative shader background",
  }

  useEffect(() => {
    if (!fill || !hostRef.current) return
    const el = hostRef.current
    const ro = new ResizeObserver(() => {
      const canvas = el.querySelector("canvas")
      if (canvas) {
        canvas.style.width = "100%"
        canvas.style.height = "100%"
      }
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [fill])

  if (fill) {
    return (
      <div
        ref={hostRef}
        className={`absolute inset-0 overflow-hidden ${className ?? ""}`}
        data-shader-slot
        {...sharedAttrs}
      />
    )
  }

  return (
    <div
      className={`w-full overflow-hidden rounded-xl bg-white dark:bg-stone-800/50 border border-stone-200 dark:border-stone-700 ${className ?? ""}`}
      style={{ minHeight, width: "100%" }}
      data-shader-slot
    >
      <div
        ref={hostRef}
        className="w-full h-full min-h-0"
        style={{ width: "100%", minHeight, height: minHeight }}
        {...sharedAttrs}
      />
    </div>
  )
}
